export type UpgradeHint = {
	phase: "adding" | "removing_source" | "cancelling";
	sourceLineId: string;
	sourceQuantity: number;
	replacementLineId?: string;
};
export type UpgradeRequest =
	| {
			checkoutId: string;
			sourceLineId: string;
			sourceVariantId: string;
			sourceQuantity: number;
			targetVariantId: string;
			channel: string;
			expectedSourcePrice: number;
			expectedTargetPrice: number;
			operationId: string;
			intent: "start";
	  }
	| {
			checkoutId: string;
			operationId: string;
			intent: "resume" | "cancel";
			resumeHint?: UpgradeHint;
			checkOnly?: boolean;
	  };
export type UpgradeResult = {
	phase?: UpgradeHint["phase"];
	status: "complete" | "rejected" | "partial" | "unknown" | "conflict" | "cancelled";
	reason?:
		"stale_offer" | "unavailable" | "add_failed" | "remove_failed" | "read_failed" | "concurrent_change";
	operationId: string;
	replacementLineId?: string;
};
export type UpgradeLine = {
	id: string;
	quantity: number;
	variantId: string;
	metadata: readonly { key: string; value: string }[];
};
export type UpgradeCheckout = { id: string; channel: string; lines: UpgradeLine[] };
type WriteOutcome = "confirmed" | "rejected" | "unknown";
export type UpgradeDependencies = {
	read: () => Promise<UpgradeCheckout | null>;
	preflight: (
		request: Extract<UpgradeRequest, { intent: "start" }>,
		checkout: UpgradeCheckout,
	) => Promise<"stale_offer" | "unavailable" | null>;
	add: (request: Extract<UpgradeRequest, { intent: "start" }>) => Promise<WriteOutcome>;
	remove: (lineId: string) => Promise<WriteOutcome>;
	validateReplacement?: (
		checkout: UpgradeCheckout,
		source: UpgradeLine,
		replacement: UpgradeLine,
	) => Promise<boolean>;
};
export function upgradeMetadata(operationId: string, sourceLineId: string, quantity: number) {
	return [
		{ key: "cart_upgrade_operation", value: operationId },
		{ key: "cart_upgrade_source_line", value: sourceLineId },
		{ key: "cart_upgrade_source_quantity", value: String(quantity) },
	];
}
function value(line: UpgradeLine, key: string) {
	const values = line.metadata.filter((m) => m.key === key);
	return values.length === 1 ? values[0].value : undefined;
}
export function findPendingUpgrade(
	checkoutId: string,
	lines: readonly UpgradeLine[],
): { checkoutId: string; operationId: string; resumeHint: UpgradeHint } | null {
	for (const line of lines) {
		const operationId = value(line, "cart_upgrade_operation"),
			sourceLineId = value(line, "cart_upgrade_source_line"),
			sourceQuantity = Number(value(line, "cart_upgrade_source_quantity"));
		if (
			operationId &&
			sourceLineId &&
			lines.some((l) => l.id === sourceLineId) &&
			Number.isSafeInteger(sourceQuantity) &&
			sourceQuantity > 0
		)
			return {
				checkoutId,
				operationId,
				resumeHint: { phase: "removing_source", sourceLineId, sourceQuantity, replacementLineId: line.id },
			};
	}
	return null;
}
/** Two writes are not atomic. Every continuation reads authoritative lines; an unknown add is never replayed. */
export async function runPackUpgrade(
	request: UpgradeRequest,
	deps: UpgradeDependencies,
): Promise<UpgradeResult> {
	let phase: UpgradeHint["phase"] =
		request.intent === "start" ? "adding" : (request.resumeHint?.phase ?? "removing_source");
	const out = (
		status: UpgradeResult["status"],
		reason?: UpgradeResult["reason"],
		replacementLineId?: string,
	): UpgradeResult => ({
		status,
		operationId: request.operationId,
		phase,
		...(reason ? { reason } : {}),
		...(replacementLineId ? { replacementLineId } : {}),
	});
	const read = async () => {
		try {
			return await deps.read();
		} catch {
			return null;
		}
	};
	const write = async (fn: () => Promise<WriteOutcome>) => {
		try {
			return await fn();
		} catch {
			return "unknown" as const;
		}
	};
	let checkout = await read();
	if (!checkout || checkout.id !== request.checkoutId) return out("unknown", "read_failed");
	let marked = checkout.lines.filter((l) => value(l, "cart_upgrade_operation") === request.operationId);
	if (marked.length > 1) return out("conflict", "concurrent_change");
	if (request.intent === "start" && marked.length === 0) {
		// A different unfinished operation cannot be silently adopted.
		if (findPendingUpgrade(checkout.id, checkout.lines)) return out("conflict", "concurrent_change");
		const source = checkout.lines.find((l) => l.id === request.sourceLineId);
		if (
			!source ||
			source.quantity !== request.sourceQuantity ||
			source.variantId !== request.sourceVariantId ||
			checkout.channel !== request.channel
		)
			return out("rejected", "stale_offer");
		const reason = await deps.preflight(request, checkout);
		if (reason) return out("rejected", reason);
		const added = await write(() => deps.add(request));
		if (added === "rejected") return out("rejected", "add_failed");
		checkout = await read();
		if (!checkout) return out("unknown", "read_failed");
		marked = checkout.lines.filter((l) => value(l, "cart_upgrade_operation") === request.operationId);
	}
	if (marked.length > 1) return out("conflict", "concurrent_change");
	const replacement = marked[0];
	if (!replacement) {
		const hint = request.intent === "start" ? undefined : request.resumeHint;
		if (
			hint?.phase === "cancelling" &&
			hint.replacementLineId &&
			!checkout.lines.some((l) => l.id === hint.replacementLineId) &&
			checkout.lines.some((l) => l.id === hint.sourceLineId && l.quantity === hint.sourceQuantity)
		)
			return out("cancelled");
		return out("unknown");
	}
	const sourceId = value(replacement, "cart_upgrade_source_line");
	const quantity = Number(value(replacement, "cart_upgrade_source_quantity"));
	if (
		!sourceId ||
		sourceId === replacement.id ||
		!Number.isSafeInteger(quantity) ||
		quantity < 1 ||
		replacement.quantity !== quantity
	)
		return out("conflict", "concurrent_change", replacement.id);
	if (
		request.intent === "start" &&
		(sourceId !== request.sourceLineId ||
			quantity !== request.sourceQuantity ||
			replacement.variantId !== request.targetVariantId)
	)
		return out("conflict", "concurrent_change", replacement.id);
	let source = checkout.lines.find((l) => l.id === sourceId);
	if (!source) return out("complete", undefined, replacement.id);
	// A still-present source does not prove its previous delete failed. Never
	// remove the replacement or acknowledge this cart while that write may finish.
	if (phase === "removing_source" || phase === "cancelling") return out("unknown", undefined, replacement.id);
	if (source.quantity !== quantity) return out("conflict", "concurrent_change", replacement.id);
	// Start deliberately stops after Add/readback. The browser persists the
	// removing_source phase before a separate explicit resume sends the delete.
	if (request.intent === "start") return out("partial", undefined, replacement.id);
	if (request.intent === "resume" && request.checkOnly) return out("partial", undefined, replacement.id);
	if (deps.validateReplacement && !(await deps.validateReplacement(checkout, source, replacement)))
		return out("conflict", "concurrent_change", replacement.id);
	const sourceVariantId = source.variantId;
	// Narrow the TOCTOU window, but no server-wide lock or atomicity is claimed.
	checkout = await read();
	if (!checkout) return out("unknown", "read_failed", replacement.id);
	const fresh = checkout.lines.filter((l) => value(l, "cart_upgrade_operation") === request.operationId);
	source = checkout.lines.find((l) => l.id === sourceId);
	if (!source && fresh.length === 1 && fresh[0].id === replacement.id && fresh[0].quantity === quantity)
		return out("complete", undefined, replacement.id);
	if (
		!source ||
		source.quantity !== quantity ||
		source.variantId !== sourceVariantId ||
		fresh.length !== 1 ||
		fresh[0].id !== replacement.id ||
		fresh[0].quantity !== quantity ||
		fresh[0].variantId !== replacement.variantId ||
		value(fresh[0], "cart_upgrade_source_line") !== sourceId ||
		Number(value(fresh[0], "cart_upgrade_source_quantity")) !== quantity
	)
		return out("conflict", "concurrent_change", replacement.id);
	const cancelling =
		request.intent === "cancel" ||
		(request.intent === "resume" && request.resumeHint?.phase === "cancelling");
	phase = cancelling ? "cancelling" : "removing_source";
	const removed = await write(() => deps.remove(cancelling ? replacement.id : sourceId));
	if (removed === "rejected") {
		phase = "adding";
		return out("partial", "remove_failed", replacement.id);
	}
	const after = await read();
	if (!after) return out("unknown", "read_failed", replacement.id);
	const sourceAfter = after.lines.find((l) => l.id === sourceId),
		replacementAfter = after.lines.find((l) => l.id === replacement.id);
	if (
		!sourceAfter &&
		replacementAfter?.quantity === quantity &&
		replacementAfter.variantId === replacement.variantId
	)
		return out("complete", undefined, replacement.id);
	if (cancelling && !replacementAfter && sourceAfter?.quantity === quantity) return out("cancelled");
	if (sourceAfter?.quantity !== quantity || replacementAfter?.quantity !== quantity)
		return out("conflict", "concurrent_change", replacement.id);
	return out("unknown", undefined, replacement.id);
}
